import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Loader, AlertCircle, FileText, Store, Package, Users, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGlobalSearch } from '../../hooks/admin/useGlobalSearch';
import { SearchResultDto, SearchResultType } from '../../types/modules/search.type';

const GlobalSearch: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [keyword, setKeyword] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { data: searchResponse, isLoading, error } = useGlobalSearch({
        keyword: debouncedKeyword,
        page: currentPage,
        enabled: isOpen,
    });

    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        if (keyword.length === 0) {
            setDebouncedKeyword('');
            setIsOpen(false);
            setSelectedIndex(-1);
            setCurrentPage(1);
            return;
        }

        debounceTimer.current = setTimeout(() => {
            setDebouncedKeyword(keyword);
            setCurrentPage(1);
            if (keyword.trim().length >= 2) {
                setIsOpen(true);
                setSelectedIndex(-1);
            }
        }, 300); // 300ms debounce

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [keyword]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Get icon for result type
    const getIconForType = (type: SearchResultType) => {
        const iconProps = { size: 16, className: 'flex-shrink-0' };
        switch (type) {
            case 'customer':
                return <Users {...iconProps} className="text-blue-500" />;
            case 'store':
                return <Store {...iconProps} className="text-green-500" />;
            case 'product':
                return <Package {...iconProps} className="text-purple-500" />;
            case 'owner':
                return <User {...iconProps} className="text-orange-500" />;
            case 'page':
                return <FileText {...iconProps} className="text-gray-500" />;
            default:
                return <Search {...iconProps} />;
        }
    };

    const getBadgeLabel = (type: SearchResultType): string => {
        const labels: Record<SearchResultType, string> = {
            customer: t('search_badge_customer') || 'Customer',
            store: t('search_badge_store') || 'Store',
            product: t('search_badge_product') || 'Product',
            owner: t('search_badge_owner') || 'Owner',
            page: t('search_badge_page') || 'Page',
        };
        return labels[type];
    };

    const getBadgeColor = (type: SearchResultType): string => {
        const colors: Record<SearchResultType, string> = {
            customer: 'bg-blue-100 text-blue-800',
            store: 'bg-green-100 text-green-800',
            product: 'bg-purple-100 text-purple-800',
            owner: 'bg-orange-100 text-orange-800',
            page: 'bg-gray-100 text-gray-800',
        };
        return colors[type];
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const results = searchResponse?.data || [];

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) =>
                    prev < results.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) =>
                    prev > 0 ? prev - 1 : results.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && results[selectedIndex]) {
                    handleSelectResult(results[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setKeyword('');
                inputRef.current?.blur();
                break;
            default:
                break;
        }
    };

    const handleSelectResult = useCallback((result: SearchResultDto) => {
        navigate(result.route);
        setIsOpen(false);
        setKeyword('');
        setSelectedIndex(-1);
    }, [navigate]);

    const groupedResults = (searchResponse?.data || []).reduce(
        (acc, result) => {
            if (!acc[result.type]) {
                acc[result.type] = [];
            }
            acc[result.type].push(result);
            return acc;
        },
        {} as Record<SearchResultType, SearchResultDto[]>
    );

    const results = searchResponse?.data || [];

    return (
        <div className="relative" ref={containerRef}>
            <div className="relative flex items-center">
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={t('search_global_placeholder') || 'Search...'}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (keyword.trim().length >= 2) {
                            setIsOpen(true);
                        }
                    }}
                    className="w-80 px-5 py-3 pl-11 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition shadow-sm"
                />
                <Search className="absolute left-3 text-gray-400" size={16} />
            </div>

            {isOpen && keyword.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[500px] overflow-y-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center py-8 px-4">
                            <Loader className="animate-spin text-primary" size={20} />
                            <span className="ml-2 text-sm text-gray-600">
                                {t('search_loading') || 'Searching...'}
                            </span>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center gap-3 py-4 px-4 text-red-600">
                            <AlertCircle size={18} />
                            <span className="text-sm">{t('search_error') || 'Error occurred during search'}</span>
                        </div>
                    )}

                    {!isLoading && !error && results.length === 0 && (
                        <div className="py-8 px-4 text-center text-gray-500 text-sm">
                            {t('search_no_results') || 'No results found'}
                        </div>
                    )}

                    {!isLoading && !error && results.length > 0 && (
                        <div className="py-2">
                            {(Object.keys(groupedResults) as SearchResultType[]).map((type) => (
                                <div key={type}>
                                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                        {getBadgeLabel(type)}
                                    </div>

                                    {groupedResults[type].map((result, idx) => {
                                        const globalIndex = results.indexOf(result);
                                        const isSelected = globalIndex === selectedIndex;

                                        return (
                                            <div
                                                key={`${result.type}-${result.id}`}
                                                onClick={() => handleSelectResult(result)}
                                                onMouseEnter={() => setSelectedIndex(globalIndex)}
                                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition ${isSelected
                                                    ? 'bg-primary/10 border-l-2 border-primary'
                                                    : 'hover:bg-gray-50'
                                                    }`}
                                            >
                                                {getIconForType(result.type)}

                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {result.title}
                                                    </p>
                                                </div>

                                                <span className={`px-2 py-1 text-xs font-medium rounded whitespace-nowrap ${getBadgeColor(result.type)}`}>
                                                    {getBadgeLabel(result.type)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}

                    {results.length > 0 && !isLoading && !error && (
                        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                                ⌨️ {t('search_keyboard_hint') || 'Use ↑↓ to navigate, Enter to select, Esc to close'}
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 text-green-600 font-bold hover:text-green-700 hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed transition rounded-md border border-green-200"
                                    title="Previous page"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700 min-w-fit px-3 py-2 bg-white rounded-md border border-gray-300">
                                    {t('search_page') || 'Page'} <span className="text-green-600">{currentPage}</span>
                                </span>
                                <button
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                    disabled={results.length === 0}
                                    className="p-2 text-green-600 font-bold hover:text-green-700 hover:bg-green-50 disabled:opacity-30 disabled:cursor-not-allowed transition rounded-md border border-green-200"
                                    title="Next page"
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
